import type { PlanDelCatalogo } from "@/schemas/billing";

/**
 * Leer el catálogo de planes. Código puro: los sobres y la identidad del plan
 * vigente se prueban sin montar la pantalla ni levantar un backend.
 */

/**
 * Saca el array de planes **de cualquiera de los sobres** en que puede llegar.
 *
 * El borde preserva el envelope de octopus a propósito, que puede venir pelado
 * (`[…]`), con clave (`{"plans": […]}`) o envuelto (`{"data": …}`), y las
 * combinaciones. Su comentario dice que así "el parser del consumidor no tiene
 * que saber cuál le tocó" — en la práctica el que tiene que saberlo es éste, así
 * que los contempla todos en vez de apostar a uno y romperse el día que cambie.
 *
 * Una forma que no reconoce devuelve `[]`, no una excepción: un catálogo vacío
 * se ve como "no hay planes para ofrecer", que es raro pero legible.
 */
export function desenvolverCatalogo(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  if (Array.isArray(o.plans)) return o.plans;
  if (Array.isArray(o.data)) return o.data;
  if (o.data && typeof o.data === "object") {
    const d = o.data as Record<string, unknown>;
    if (Array.isArray(d.plans)) return d.plans;
  }
  return [];
}

/**
 * ¿El plan vigente **no está** en el catálogo?
 *
 * No es un error: `GET /billing/plans` devuelve sólo los **ofrecibles**
 * (`is_public` y `status=active`), y un negocio puede estar en un plan
 * `internal` —un precio negociado con él— que por definición no está en la
 * vidriera. Ahí el vigente se dibuja **aparte** de la lista de opciones, con el
 * nombre y el precio que trae el resumen. Justamente por eso octopus manda el
 * nombre y no sólo el id.
 *
 * Con `plan_id` en `null` devuelve `false`: no sabemos cuál es, así que tampoco
 * podemos afirmar que está afuera.
 */
export function planVigenteFueraDelCatalogo(
  planes: PlanDelCatalogo[],
  planId: string | null | undefined
): boolean {
  if (!planId) return false;
  return !planes.some((p) => p.plan_id === planId);
}

/**
 * Cómo se lee un cupo del plan.
 *
 * **`null` significa SIN TOPE**, no cero — octopus usa la ausencia para
 * "ilimitado" y el borde la copia tal cual. Traducirlo a cero diría que el plan
 * no deja crear nada, que es lo contrario.
 */
export function textoDelCupo(
  valor: number | null | undefined,
  etiqueta: string
): string {
  return valor == null ? `${etiqueta} sin tope` : `hasta ${valor} ${etiqueta}`;
}

/** Una cadencia contratable: cada cuántos días se cobra, y cuánto. */
export interface Cadencia {
  interval_days: number | null;
  amount: string | null;
  currency?: string | null;
}

/**
 * Las cadencias que se le pueden ofrecer al dueño para un plan.
 *
 * # `prices` ausente NO es `prices: []`
 *
 * Contra un octopus viejo el campo **no viaja**, y el borde no inventa una lista
 * vacía: ausente es *"no sé"*, vacío sería *"este plan no cotiza ninguna
 * cadencia"*. En los dos casos lo único que sabemos es la cadencia propia del
 * plan, así que se ofrece ésa — y **nunca** se afirma que no hay anual, porque
 * eso es exactamente lo que no sabemos.
 *
 * La diferencia importa igual y por eso está escrita: el día que se quiera
 * mostrar "este plan no cotiza otras cadencias", sólo se puede decir con `[]`,
 * jamás con la ausencia.
 */
export function cadenciasDe(plan: PlanDelCatalogo): Cadencia[] {
  if (Array.isArray(plan.prices) && plan.prices.length > 0) {
    return plan.prices.map((c) => ({
      interval_days: c.interval_days,
      amount: c.amount ?? null,
      currency: c.currency ?? plan.currency ?? null,
    }));
  }
  return [
    {
      interval_days: plan.interval_days ?? null,
      amount: plan.price ?? null,
      currency: plan.currency ?? null,
    },
  ];
}

/** `30` → "mensual" · `365` → "anual" · el resto, los días tal cual. */
export function etiquetaDeCadencia(dias: number | null | undefined): string {
  if (dias === 30) return "mensual";
  if (dias === 365) return "anual";
  if (dias == null) return "cadencia actual";
  return `cada ${dias} días`;
}
